/**
 * /recruitment/offers/:id/convert — convert an ACCEPTED offer into an Employee
 * (ADR-0012 D-2/D-3/D-4). Sensitive: this writes a real Employee + Compensation
 * that payroll pays against.
 *
 * Two guardrails surfaced for the operator:
 *   - The soft rehire-warning (D-4): if the backend returns 409 REHIRE_WARNING,
 *     we show the matched existing employee and require an EXPLICIT "hire anyway"
 *     that re-submits proceedDespiteRehireWarning (audited server-side). It is
 *     never auto-bypassed or silently swallowed.
 *   - The employeeNumber 409 (taken) and the NG-stateCode 400 are shown plainly.
 *
 * HR-gated; mounted under the admin layout.
 */
import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import { convertOffer, type RehireWarning } from '../api/recruitment.js';
import { listCategories, requestAsset } from '../api/assets.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';

export const recruitmentConvertRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/recruitment/offers/$offerId/convert',
  component: RecruitmentConvertPage,
});

function isRehireWarning(body: unknown): body is RehireWarning {
  return typeof body === 'object' && body !== null && (body as { code?: unknown }).code === 'REHIRE_WARNING';
}

function RecruitmentConvertPage(): JSX.Element {
  const { offerId } = recruitmentConvertRoute.useParams();
  const navigate = useNavigate();
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rehire, setRehire] = useState<RehireWarning['match'] | null>(null);
  const [done, setDone] = useState<{ employeeNumber: string; employeeId: string } | null>(null);

  const convert = useMutation({
    mutationFn: (proceedDespiteRehireWarning: boolean) =>
      convertOffer(offerId, {
        employeeNumber: employeeNumber.trim() || undefined,
        hireDate: hireDate.trim(),
        stateCode: stateCode.trim() || undefined,
        proceedDespiteRehireWarning,
      }),
    onSuccess: (res) => { setError(null); setRehire(null); setDone({ employeeNumber: res.employee.employeeNumber, employeeId: res.employee.id }); },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409 && isRehireWarning(e.body)) {
        // The soft rehire-warning — surface the match; do NOT auto-proceed.
        setRehire(e.body.match);
        setError(null);
      } else {
        setRehire(null);
        setError(e instanceof ApiError ? e.message : 'Conversion failed.');
      }
    },
  });

  if (done) {
    return (
      <section className="p-8 space-y-4 max-w-xl">
        <h1 className="font-serif text-3xl">Hired</h1>
        <div role="status" className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">
          Employee {done.employeeNumber} created from this offer.
        </div>
        <HireSeamAssets employeeId={done.employeeId} />
        <button type="button" onClick={() => void navigate({ to: '/recruitment/candidates' })} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200">
          Back to candidates
        </button>
      </section>
    );
  }

  return (
    <section className="p-8 space-y-6 max-w-xl">
      <Link to="/recruitment/candidates" className="text-sm text-gray-400 underline">← Candidates</Link>
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Convert to employee</h1>
        <p className="text-sm text-gray-400">
          Hire this candidate from their accepted offer. The offer’s salary becomes
          their first compensation record.
        </p>
      </header>

      <form
        className="rounded border border-gray-900 p-5 space-y-4"
        onSubmit={(e) => { e.preventDefault(); if (hireDate.trim()) convert.mutate(false); }}
      >
        <label className="block text-sm text-gray-300 space-y-1"><span>Employee number</span>
          <input value={employeeNumber} onChange={(e) => setEmployeeNumber(e.target.value)} aria-label="Employee number" placeholder="Leave blank to use the offer’s number" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="block text-sm text-gray-300 space-y-1"><span>Hire date</span>
          <input type="date" value={hireDate} onChange={(e) => setHireDate(e.target.value)} aria-label="Hire date" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <label className="block text-sm text-gray-300 space-y-1"><span>State code <span className="text-gray-500">(required for Nigeria)</span></span>
          <input value={stateCode} onChange={(e) => setStateCode(e.target.value)} aria-label="State code" placeholder="e.g. LA" className="w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm" />
        </label>
        <button type="submit" disabled={!hireDate.trim() || convert.isPending} className="rounded bg-white text-black px-4 py-2 text-sm font-medium hover:bg-gray-200 disabled:opacity-50">
          {convert.isPending && !rehire ? 'Hiring…' : 'Convert to employee'}
        </button>
        {error ? <Alert variant="error">{error}</Alert> : null}
      </form>

      {rehire ? (
        <div role="alert" className="rounded border border-yellow-800 bg-yellow-950/30 p-4 space-y-3">
          <h2 className="text-sm text-yellow-200">A possible existing employee matches this candidate</h2>
          <p className="text-sm text-gray-300">
            {rehire.firstName} {rehire.lastName} <span className="font-mono text-xs text-gray-400">({rehire.employeeNumber})</span> already
            exists with the same email. This may be a rehire — or a duplicate. Hiring anyway creates a NEW employee record.
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => convert.mutate(true)} disabled={convert.isPending} className="rounded bg-yellow-700 text-white text-sm px-4 py-2 disabled:opacity-50">
              {convert.isPending ? 'Hiring…' : 'Hire anyway'}
            </button>
            <button type="button" onClick={() => setRehire(null)} className="rounded border border-gray-700 text-sm px-4 py-2 text-gray-300 hover:bg-gray-900">
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

/**
 * The hire seam (ADR-0024 Phase 4 / 3a amendment): after the hire, HR selects
 * the org's active asset categories to equip the new employee. One requisition
 * per selection — they're independent, so PARTIAL SUCCESS is a normal outcome
 * (a manager may later approve the laptop and reject the car). We show what was
 * created and what failed, and let HR retry failures. Nothing is rolled back.
 */
function HireSeamAssets({ employeeId }: { employeeId: string }): JSX.Element {
  const cats = useQuery({ queryKey: ['asset', 'categories'], queryFn: listCategories });
  const active = (cats.data?.categories ?? []).filter((c) => c.active);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Record<string, { ok: boolean; error?: string }>>({});
  const [busy, setBusy] = useState(false);

  const toggle = (id: string): void =>
    setSelected((s) => { const next = new Set(s); if (next.has(id)) next.delete(id); else next.add(id); return next; });

  async function raise(categoryIds: string[]): Promise<void> {
    setBusy(true);
    const settled = await Promise.allSettled(categoryIds.map((categoryId) => requestAsset({ employeeId, categoryId })));
    setResults((prev) => {
      const next = { ...prev };
      categoryIds.forEach((categoryId, i) => {
        const r = settled[i]!;
        next[categoryId] = r.status === 'fulfilled' ? { ok: true } : { ok: false, error: r.reason instanceof ApiError ? r.reason.message : 'Failed' };
      });
      return next;
    });
    setSelected(new Set());
    setBusy(false);
  }

  const failed = Object.entries(results).filter(([, r]) => !r.ok).map(([id]) => id);
  if (active.length === 0) return <p className="text-sm text-gray-500">No asset categories configured — set them up to equip new hires.</p>;

  return (
    <div className="rounded border border-gray-900 p-4 space-y-3">
      <h2 className="text-sm font-medium text-gray-200">Equip this hire (optional)</h2>
      <p className="text-xs text-gray-400">Select what the new employee needs. Each raises a requisition routed to their manager for approval.</p>
      <div className="flex flex-wrap gap-2">
        {active.map((c) => {
          const res = results[c.id];
          return (
            <label key={c.id} className={`flex items-center gap-2 rounded border px-3 py-1.5 text-sm ${res?.ok ? 'border-green-900 bg-green-950/30 text-green-300' : res && !res.ok ? 'border-red-900 bg-red-950/30 text-red-300' : 'border-gray-700 text-gray-300'}`}>
              {!res?.ok ? <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} aria-label={`Requisition ${c.label}`} /> : null}
              <span>{c.label}</span>
              {res?.ok ? <span className="text-xs">✓ raised</span> : null}
              {res && !res.ok ? <span className="text-xs">✕ failed</span> : null}
            </label>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <button type="button" disabled={selected.size === 0 || busy} onClick={() => void raise([...selected])} className="rounded bg-white text-black px-3 py-1.5 text-sm font-medium disabled:opacity-50">
          {busy ? 'Raising…' : `Raise ${selected.size || ''} requisition${selected.size === 1 ? '' : 's'}`}
        </button>
        {failed.length > 0 ? (
          <button type="button" disabled={busy} onClick={() => void raise(failed)} className="rounded border border-red-800 text-red-300 px-3 py-1.5 text-sm disabled:opacity-50">
            Retry {failed.length} failed
          </button>
        ) : null}
      </div>
      {Object.values(results).some((r) => r.ok) && failed.length > 0 ? (
        <p role="status" className="text-xs text-gray-400">Some requisitions were raised; some failed — retry the failures above. Nothing was undone.</p>
      ) : null}
    </div>
  );
}
